/*eslint @typescript-eslint/no-empty-function: "off"*/
import React from 'react';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';
import { RouteComponentProps } from '@reach/router';
import { IProviders, IUsers, RootState } from 'store/store';
import PermissionSchemesList from './PermissionSchemesList';
import PermissionSchemeInput from './PermissionSchemeInput';
import { display } from 'components/ErrorView/ErrorDialog';
import { PermissionSchemesContainer } from './styles';
import {
  requireProviderTags,
  requireProviderTagsFn,
} from 'store/providers/providerSlice';
import Collapsible from '../../../../components/Collapsible/Collapsible';
import { appDispatch } from 'store/store';
import {
  ICompany,
  ICompanyPermissionScheme,
} from 'store/companies/companiesTypes';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import {
  addCompanyPermissionScheme,
  addCompanyPermissionSchemeFn,
  deleteCompanyPermissionScheme,
  deleteCompanyPermissionSchemeFn,
  updateCompanyPermissionScheme,
  updateCompanyPermissionSchemeFn,
} from 'store/companies/companiesSlice';
import i18n from '../../../../i18n';

interface IStateToProps {
  allTags: IProviders['tags'];
  companyProvidersIdName: [number, string][];
  company: ICompany;
  thisUser: IThisUserState;
  users: IUsers;
}

interface IDispatchToProps {
  addPermissionScheme: (
    permissionScheme: ICompanyPermissionScheme
  ) => ReturnType<typeof addCompanyPermissionSchemeFn>;
  updatePermissionScheme: (
    permissionScheme: ICompanyPermissionScheme
  ) => ReturnType<typeof updateCompanyPermissionSchemeFn>;
  deletePermissionScheme: (
    permissionSchemeId: number,
    companyId: number
  ) => ReturnType<typeof deleteCompanyPermissionSchemeFn>;
  requireProviderTags: (id: number) => ReturnType<typeof requireProviderTagsFn>;
}

interface IOwnProps {
  companyId?: string;
}

type IComponentProps = IOwnProps &
  IStateToProps &
  IDispatchToProps &
  RouteComponentProps;

interface ICompanyPermissionSchemesState {
  loading: boolean;
}

class CompanyPermissionSchemes extends React.Component<
  IComponentProps,
  ICompanyPermissionSchemesState
> {
  constructor(props: IComponentProps) {
    super(props);
    this.state = {
      loading: false,
    };
  }

  componentDidMount() {
    this.props.companyProvidersIdName.forEach(([id, _]) => {
      if (this.props.allTags[id]?.status !== 'loaded') {
        this.props.requireProviderTags(id);
      }
    });
  }

  deletePermissionSchemeItem =
    (permissionSchemeId: number, companyId: number) => () => {
      this.props
        .deletePermissionScheme(permissionSchemeId, companyId)
        .then(() => {
          this.setState({
            loading: false,
          });
        })
        .catch((err: any) => {
          this.setState({ loading: false });
          display(err);
        });
    };

  addPermissionSchemeItem = (item: ICompanyPermissionScheme) => {
    this.setState({ loading: true });
    return this.props
      .addPermissionScheme(item)
      .then(() => {
        this.setState({ loading: false });
      })
      .catch((err: any) => {
        this.setState({ loading: false });
        display(err);
      });
  };

  updatePermissionSchemeItem = (item: ICompanyPermissionScheme) => {
    return this.props.updatePermissionScheme(item);
  };

  render() {
    const { loading } = this.state;
    const { company, companyId } = this.props;
    const permissionSchemes = company.permissionSchemes;

    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <PermissionSchemesContainer>
          <Collapsible
            defaultOpen={!Object.values(permissionSchemes).length}
            header={i18n.t('addPermissionSchemeToCompany', {
              ns: 'companyManagement',
            })}
          >
            <PermissionSchemeInput
              addOrUpdatePermissionScheme={this.addPermissionSchemeItem}
              loading={loading}
              companyId={Number(companyId)}
              company={company}
              cancelEdit={() => {}}
              allTags={this.props.allTags}
              companyProvidersIdName={this.props.companyProvidersIdName}
            />
          </Collapsible>
          <PermissionSchemesList
            permissionSchemes={permissionSchemes}
            deletePermissionScheme={this.deletePermissionSchemeItem}
            updatePermissionScheme={this.updatePermissionSchemeItem}
            allTags={this.props.allTags}
            companyProvidersIdName={this.props.companyProvidersIdName}
            company={company}
          />
        </PermissionSchemesContainer>
      </div>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  const companyId = Number(props.companyId);
  const company = state.companies.data[companyId];
  const thisUser = state.thisUser;
  const users = state.users;

  const companyProvidersIdName = Object.values(state.providers.map).reduce(
    (current: any, item: any) => {
      if (item.data.managingCompanyId === companyId)
        current.push([item.data.id, item.data.name]);
      return current;
    },
    []
  );

  const allTags = state.providers.tags;

  return { users, company, thisUser, companyProvidersIdName, allTags };
};

const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  IOwnProps
> = () => {
  return {
    addPermissionScheme: (permissionScheme) =>
      appDispatch(addCompanyPermissionScheme({ role: permissionScheme })),
    updatePermissionScheme: (permissionScheme) =>
      appDispatch(updateCompanyPermissionScheme({ role: permissionScheme })),
    deletePermissionScheme: (permissionSchemeId: number, companyId: number) =>
      appDispatch(
        deleteCompanyPermissionScheme({ roleId: permissionSchemeId, companyId })
      ),
    requireProviderTags: (id: number) =>
      appDispatch(requireProviderTags({ providerId: id })),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CompanyPermissionSchemes);
